import { useStoreActions, useStoreState } from '@/stores/store';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import SelectButton from '@/components/ui/therapist/SelectButton/SelectButton';
import { getFullExerciseName } from '../../utils/exerciseNames';
import colors from '@/config/colors';
import SpeedDropdown from '@/components/SpeedDropdown';
import { Divider } from 'react-native-elements';
import { getPlaybackRate } from '@/utils/getPlaybackRate';

const styles = StyleSheet.create({
  subheading: { fontSize: 20, fontWeight: '600', marginBottom: 12 },
  selectLabel: { fontSize: 16, fontWeight: '400', marginBottom: 6 },
  selectContainer: { flexDirection: 'row', gap: 32, alignItems: 'flex-end' },
  divider: {
    width: '100%',
    height: 1,
    backgroundColor: colors.newUI.therapist.primaryBlue10,
    marginVertical: 8,
  },
});

function DesignateInWriting() {
  const updateSettings = useStoreActions((actions) => actions.updateSettings);
  const copyWordsetExerciseTypes = useStoreState(
    (state) => state.copyWordsetExerciseTypes,
  );
  const copyTextsetExerciseTypes = useStoreState(
    (state) => state.copyTextsetExerciseTypes,
    (prev, next) => {
      if (
        prev[2]?.setting !== next[2]?.setting ||
        prev[2]?.playbackRate !== next[2]?.playbackRate
      ) {
        return false;
      } else {
        return true;
      }
    },
  );
  const copySentencesetExerciseTypes = useStoreState(
    (state) => state.copySentencesetExerciseTypes,
    (prev, next) => {
      if (
        prev[2]?.setting !== next[2]?.setting ||
        prev[2]?.value !== next[2]?.value ||
        prev[2]?.playbackRate !== next[2]?.playbackRate
      ) {
        return false;
      } else {
        return true;
      }
    },
  );

  return (
    <View>
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Wörter</Text>
        <View style={styles.selectContainer}>
          <View>
            <Text style={styles.selectLabel}>Art der Wörter</Text>
            <SelectButton
              options={[
                { label: 'Ganze Tastatur', value: 'full' },
                { label: 'Lückenwort', value: 'gap' },
                { label: 'Anagramme', value: 'anagram' },
              ]}
              value={
                copyWordsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Writing', 'wordset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'wordset',
                  exerciseName: getFullExerciseName('Writing', 'wordset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
          {copyWordsetExerciseTypes.some(
            (settings) => settings?.setting === 'gap',
          ) && (
            <View style={{ flex: 1 }}>
              <Text style={styles.selectLabel}>Anzahl Lücken</Text>
              <SelectButton
                options={[
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                ]}
                value={
                  copyWordsetExerciseTypes.find(
                    ({ name }) =>
                      name === getFullExerciseName('Writing', 'wordset'),
                  )?.value
                }
                onValueChange={(newValue) =>
                  updateSettings({
                    newValue,
                    setType: 'wordset',
                    exerciseName: getFullExerciseName('Writing', 'wordset'),
                    fieldToBeUpdated: 'value',
                  })
                }
              />
            </View>
          )}
          {copyWordsetExerciseTypes.some(
            (settings) => settings?.setting === 'anagram',
          ) && (
            <View style={{ flex: 1 }}>
              <Text style={styles.selectLabel}>Anzahl Zusatzgrapheme</Text>
              <SelectButton
                options={[
                  { label: '0', value: 0 },
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                ]}
                value={
                  copyWordsetExerciseTypes.find(
                    ({ name }) =>
                      name === getFullExerciseName('Writing', 'wordset'),
                  )?.value
                }
                onValueChange={(newValue) =>
                  updateSettings({
                    newValue,
                    setType: 'wordset',
                    exerciseName: getFullExerciseName('Writing', 'wordset'),
                    fieldToBeUpdated: 'value',
                  })
                }
              />
            </View>
          )}
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(copyWordsetExerciseTypes, 'Writing', 'wordset'),
          )}
          exerciseName="Writing"
          setType="wordset"
        />
      </View>
      <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Sätze</Text>
        <View style={styles.selectContainer}>
          <View>
            <Text style={styles.selectLabel}>Art der Sätze</Text>
            <SelectButton
              options={[
                { label: 'Ganze Tastatur', value: 'full' },
                { label: 'Lückensatz', value: 'gap' },
                { label: 'Anagramme', value: 'anagram' },
              ]}
              value={
                copySentencesetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Writing', 'sentenceset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'sentenceset',
                  exerciseName: getFullExerciseName('Writing', 'sentenceset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
          {copySentencesetExerciseTypes.some(
            (settings) => settings?.setting === 'gap',
          ) && (
            <View style={{ flex: 1 }}>
              <Text style={styles.selectLabel}>Anzahl Lücken</Text>
              <SelectButton
                options={[
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                ]}
                value={
                  copySentencesetExerciseTypes.find(
                    ({ name }) =>
                      name === getFullExerciseName('Writing', 'sentenceset'),
                  )?.value
                }
                onValueChange={(newValue) =>
                  updateSettings({
                    newValue,
                    setType: 'sentenceset',
                    exerciseName: getFullExerciseName('Writing', 'sentenceset'),
                    fieldToBeUpdated: 'value',
                  })
                }
              />
            </View>
          )}
          {copySentencesetExerciseTypes.some(
            (settings) => settings?.setting === 'anagram',
          ) && (
            <View style={{ flex: 1 }}>
              <Text style={styles.selectLabel}>Anzahl Zusatzwörter</Text>
              <SelectButton
                options={[
                  { label: '0', value: 0 },
                  { label: '1', value: 1 },
                  { label: '2', value: 2 },
                  { label: '3', value: 3 },
                  { label: '4', value: 4 },
                ]}
                value={
                  copySentencesetExerciseTypes.find(
                    ({ name }) =>
                      name === getFullExerciseName('Writing', 'sentenceset'),
                  )?.value
                }
                onValueChange={(newValue) =>
                  updateSettings({
                    newValue,
                    setType: 'sentenceset',
                    exerciseName: getFullExerciseName('Writing', 'sentenceset'),
                    fieldToBeUpdated: 'value',
                  })
                }
              />
            </View>
          )}
        </View>
        <SpeedDropdown
          updateSettings={updateSettings}
          value={String(
            getPlaybackRate(
              copySentencesetExerciseTypes,
              'Writing',
              'sentenceset',
            ),
          )}
          exerciseName="Writing"
          setType="sentenceset"
        />
      </View>
      <Divider width={1} color={colors.newUI.therapist.primaryBlue10} />
      <View style={{ padding: 20 }}>
        <Text style={styles.subheading}>Texte</Text>
        <View style={styles.selectContainer}>
          <View style={{ flex: 1 }}>
            <Text style={styles.selectLabel}>Art des Texts</Text>
            <SelectButton
              options={[
                { label: 'Lückentext', value: 'gap' },
                { label: 'Diktat schreiben', value: 'diktat' },
              ]}
              value={
                copyTextsetExerciseTypes.find(
                  ({ name }) =>
                    name === getFullExerciseName('Writing', 'textset'),
                )?.setting
              }
              onValueChange={(newValue) =>
                updateSettings({
                  newValue,
                  setType: 'textset',
                  exerciseName: getFullExerciseName('Writing', 'textset'),
                  fieldToBeUpdated: 'setting',
                })
              }
            />
          </View>
        </View>
        {copyTextsetExerciseTypes.some(
          (textExercise) => textExercise.setting === 'diktat',
        ) && (
          <SpeedDropdown
            updateSettings={updateSettings}
            value={String(
              getPlaybackRate(copyTextsetExerciseTypes, 'Writing', 'textset'),
            )}
            exerciseName="Writing"
            setType="textset"
          />
        )}
      </View>
    </View>
  );
}

export default DesignateInWriting;
